// Validaciones y formatos del modal "Generar Carátula" (components/Caratula/base). Son las
// mismas reglas que aplica el backend en CaratulaDatosClienteService: el frontend bloquea antes
// de enviar y el backend responde 400 si algo se escapa.

const PATRON_MAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_BYTES_CLAVE = 60;

export const esEmailValido = (valor) => PATRON_MAIL.test((valor || '').trim());

/** Inserta las barras mientras se tipea: "1406196" → "14/06/196". Solo dígitos, máximo 8. */
export const formatearFechaMientrasEscribe = (valor) => {
    const n = (valor || '').replace(/\D/g, '');
    if (!n.length) return '';
    if (n.length <= 2) return n;
    if (n.length <= 4) return `${n.slice(0, 2)}/${n.slice(2)}`;
    return `${n.slice(0, 2)}/${n.slice(2, 4)}/${n.slice(4, 8)}`;
};

/** DD/MM/AAAA que existe de verdad (31/02 no). Devuelve el Date o null. */
export const parsearFechaCaratula = (valor) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((valor || '').trim());
    if (!m) return null;
    const [dia, mes, anio] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (anio < 1900 || anio > 2100) return null;
    const fecha = new Date(anio, mes - 1, dia);
    if (fecha.getFullYear() !== anio || fecha.getMonth() !== mes - 1 || fecha.getDate() !== dia) {
        return null;
    }
    return fecha;
};

export const esFechaValida = (valor) => parsearFechaCaratula(valor) !== null;

/** Bytes UTF-8: las claves se guardan cifradas y el límite real es en bytes, no en caracteres. */
const bytesUtf8 = (valor) => unescape(encodeURIComponent(valor || '')).length;

/**
 * Renglón impreso a partir de las partes, idéntico a formatearDomicilio del backend:
 * "CALLE ALTURA – B° BARRIO – LOCALIDAD" (el barrio viene de la base, el modal no lo edita).
 */
export const componerDomicilio = (datos) => {
    if (!datos) return '';
    let texto = (datos.calle || '').trim();
    if (`${datos.altura ?? ''}`.trim()) texto += ` ${`${datos.altura}`.trim()}`;
    if ((datos.barrio || '').trim()) texto += ` – B° ${datos.barrio.trim()}`;
    if ((datos.localidad || '').trim()) texto += ` – ${datos.localidad.trim()}`;
    return texto.trim();
};

/** Varios domicilios en un renglón (Reajuste Badaro: "DNI / REAL"), como formatearDomicilioDniYReal. */
export const componerDomicilios = (lista) => {
    const partes = lista.map(componerDomicilio).filter(Boolean);
    const unicas = partes.filter((p, i) => partes.findIndex((o) => o.toLowerCase() === p.toLowerCase()) === i);
    return unicas.join(' / ');
};

const tieneAlgo = (d) => Boolean(d && ((d.calle || '').trim() || `${d.altura ?? ''}`.trim() || d.idLocalidad));

/** Error de un domicilio estructurado, o null. Vacío del todo es válido (no se toca). */
export const errorDomicilio = (datos) => {
    if (!tieneAlgo(datos)) return null;
    if (!(datos.calle || '').trim()) return 'Falta la calle.';
    const altura = `${datos.altura ?? ''}`.trim();
    if (altura && !/^\d{1,6}$/.test(altura)) return 'El número de calle tiene que ser numérico.';
    if (!datos.idLocalidad) return 'Elegí la localidad de la lista.';
    return null;
};

/**
 * Errores del formulario del modal: { claveDelCampo: mensaje }. Solo se validan los renglones
 * con tipo de formato (fecha, email, clave, domicilio); vacío nunca es error.
 */
export const validarFormulario = (campos, form) => {
    const errores = {};
    if (!campos || !form) return errores;
    Object.entries(campos).forEach(([key, campo]) => {
        const valor = form[campo.campo || key];
        const texto = typeof valor === 'string' ? valor.trim() : '';
        if (campo.tipo === 'fecha' && texto) {
            const fecha = parsearFechaCaratula(texto);
            if (!fecha) {
                errores[key] = 'Fecha incompleta o inválida (DD/MM/AAAA).';
            } else if (campo.noFutura && fecha > new Date()) {
                errores[key] = 'No puede ser una fecha futura.';
            }
        }
        if (campo.tipo === 'email' && texto && !esEmailValido(texto)) {
            errores[key] = 'Correo inválido (ej: nombre@dominio.com).';
        }
        if (campo.tipo === 'clave' && bytesUtf8(valor) > MAX_BYTES_CLAVE) {
            errores[key] = `Demasiado larga (máximo ${MAX_BYTES_CLAVE} caracteres).`;
        }
        if (campo.tipo === 'domicilio') {
            const estructurados = [].concat(campo.estructurado || []);
            const error = estructurados.map((k) => errorDomicilio(form[k])).find(Boolean);
            if (error) errores[key] = error;
        }
    });
    return errores;
};
